import { useCallback, useEffect, useRef, useState } from 'react';
import { MESSAGE_TYPE } from '../../../../../shared/utils/matrix/matrixRoomUtils';

/**
 * Walk the room timeline in order and derive the server-time (ms) when the
 * current in-progress call of this type started, using m.notice messages:
 * "Started {voice|video} call" / "Ended {voice|video} call - ...".
 * Both peers see the same timeline, so duration stays in sync across refresh.
 *
 * @param {import('matrix-js-sdk').Room | null} matrixRoom
 * @param {'voice' | 'video'} callTypeLabel
 * @returns {number | null} origin_server_ts in ms, or null if unknown
 */
export function getActiveCallWallStartMs(matrixRoom, callTypeLabel) {
  if (!matrixRoom || typeof matrixRoom.getLiveTimeline !== 'function') return null;

  let timeline;
  try {
    timeline = matrixRoom.getLiveTimeline();
  } catch (_) {
    return null;
  }

  const events = timeline?.getEvents?.() || [];
  if (!Array.isArray(events) || events.length === 0) return null;

  const startedBody = `Started ${callTypeLabel} call`;
  const endedPrefix = `Ended ${callTypeLabel} call`;

  let activeStartMs = null;

  for (let i = 0; i < events.length; i += 1) {
    const ev = events[i];
    if (!ev || typeof ev.getType !== 'function') continue;
    if (ev.getType() !== MESSAGE_TYPE) continue;

    const c = ev.getContent?.() || {};
    if (c.msgtype !== 'm.notice') continue;

    const body = c.body;
    if (typeof body !== 'string') continue;

    if (body === startedBody) {
      const ts = ev.getTs?.();
      if (typeof ts === 'number' && Number.isFinite(ts)) {
        activeStartMs = ts;
      }
    } else if (body.startsWith(endedPrefix)) {
      activeStartMs = null;
    }
  }

  return activeStartMs;
}

/**
 * Call duration: local tick + pause when disconnected, anchored to Matrix
 * "Started … call" server time so both peers match after refresh.
 *
 * @param {object} params
 * @param {import('matrix-js-sdk').MatrixClient | null | undefined} params.client
 * @param {import('matrix-js-sdk').Room | null} params.matrixRoom
 * @param {object | null | undefined} params.activeCall
 * @param {'voice' | 'video'} params.callTypeLabel
 * @param {string | null | undefined} params.roomId
 * @param {string} params.callType
 * @param {string} params.callUiType
 */
export function useCallDurationTimer({
  client,
  matrixRoom,
  activeCall,
  callTypeLabel,
  roomId,
  callType,
  callUiType,
}) {
  const [callDurationSec, setCallDurationSec] = useState(0);

  const durationIntervalRef = useRef(null);
  const durationStartAtRef = useRef(null);
  /** Seconds counted while both peers were connected before a pause (ICE/offline). */
  const durationAccumulatedSecRef = useRef(0);
  /** True once we've applied server wall time for the current segment (or no-op aligned). */
  const timelineAnchorConsumedRef = useRef(false);
  const lastDurationCallIdRef = useRef(null);

  useEffect(() => {
    timelineAnchorConsumedRef.current = false;
  }, [roomId, callType, callUiType]);

  useEffect(() => {
    const currentCallId = activeCall?.callId ?? null;
    const previousCallId = lastDurationCallIdRef.current;
    if (currentCallId && previousCallId !== currentCallId) {
      timelineAnchorConsumedRef.current = false;
    }
    lastDurationCallIdRef.current = currentCallId;
  }, [activeCall?.callId]);

  // Re-anchor from timeline when "Started … call" arrives or Matrix sync completes.
  useEffect(() => {
    if (!client || !matrixRoom || !activeCall || activeCall.callHasEnded?.()) return;

    const roomIdForRoom = matrixRoom.roomId;
    const tryHydrateFromTimeline = () => {
      if ((durationAccumulatedSecRef.current || 0) !== 0) return;
      if (typeof durationStartAtRef.current !== 'number') return;

      const wallMs = getActiveCallWallStartMs(matrixRoom, callTypeLabel);
      if (wallMs == null || wallMs > Date.now()) return;

      const current = durationStartAtRef.current;
      if (Math.abs(current - wallMs) <= 1) {
        timelineAnchorConsumedRef.current = true;
        return;
      }

      durationStartAtRef.current = wallMs;
      timelineAnchorConsumedRef.current = true;
      setCallDurationSec(Math.max(0, Math.floor((Date.now() - wallMs) / 1000)));
    };

    const onRoomTimeline = (event, room) => {
      if (room?.roomId !== roomIdForRoom) return;
      tryHydrateFromTimeline();
    };

    const onClientSync = () => {
      tryHydrateFromTimeline();
    };

    tryHydrateFromTimeline();
    client.on?.('Room.timeline', onRoomTimeline);
    client.on?.('sync', onClientSync);
    return () => {
      client.removeListener?.('Room.timeline', onRoomTimeline);
      client.removeListener?.('sync', onClientSync);
    };
  }, [client, matrixRoom, activeCall, callTypeLabel]);

  const clearDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const pauseDurationTimer = useCallback(() => {
    const startedAt = durationStartAtRef.current;
    if (typeof startedAt !== 'number') return;
    const elapsedSec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    durationAccumulatedSecRef.current = (durationAccumulatedSecRef.current || 0) + elapsedSec;
    durationStartAtRef.current = null;
    clearDurationTimer();
    setCallDurationSec(Math.max(0, durationAccumulatedSecRef.current || 0));
  }, [clearDurationTimer]);

  const resumeDurationTimer = useCallback(() => {
    if (!activeCall || activeCall.callHasEnded?.()) return;
    if (durationIntervalRef.current) return;
    const now = Date.now();
    let startAt = now;
    if (
      !timelineAnchorConsumedRef.current &&
      (durationAccumulatedSecRef.current || 0) === 0 &&
      matrixRoom
    ) {
      const wallMs = getActiveCallWallStartMs(matrixRoom, callTypeLabel);
      if (wallMs != null && wallMs <= now) {
        startAt = wallMs;
        timelineAnchorConsumedRef.current = true;
      }
    }
    if (typeof durationStartAtRef.current === 'number') {
      startAt = durationStartAtRef.current;
    }
    durationStartAtRef.current = startAt;
    const initialElapsed = Math.max(
      0,
      Math.floor((now - startAt) / 1000) + (durationAccumulatedSecRef.current || 0)
    );
    setCallDurationSec(initialElapsed);

    durationIntervalRef.current = setInterval(() => {
      const startedAtInner = durationStartAtRef.current || Date.now();
      const elapsedSec = Math.max(0, Math.floor((Date.now() - startedAtInner) / 1000));
      const totalSec = (durationAccumulatedSecRef.current || 0) + elapsedSec;
      setCallDurationSec(Math.max(0, totalSec));
    }, 1000);
  }, [activeCall, matrixRoom, callTypeLabel]);

  const resetDurationTimer = useCallback(() => {
    durationAccumulatedSecRef.current = 0;
    durationStartAtRef.current = null;
    timelineAnchorConsumedRef.current = false;
    clearDurationTimer();
    setCallDurationSec(0);
  }, [clearDurationTimer]);

  return {
    callDurationSec,
    durationAccumulatedSecRef,
    durationStartAtRef,
    pauseDurationTimer,
    resumeDurationTimer,
    resetDurationTimer,
  };
}
